import type { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import type { Block, BlockChange } from '@basehub/data/replicache/index'
import {
  isBlockWithChildren,
  isComponentBlock,
  isListBlock,
  isRichTextBlock,
  shouldInheritHiddenState,
  DEFAULT_LIST_VIEW,
} from '@basehub/data/replicache/index'
import * as TreePrimitive from '~/sotano-kit/primitives/tree'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { ContextMenuActions } from './context-menu-actions'
import * as React from 'react'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { invariant } from '@epic-web/invariant'
import { BlockIcon } from '~/components/block-icon'
import { clsx } from 'clsx'
import { EyeClosedIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import {
  useIsMemberOrHigher,
  useIsEditorOrHigher,
} from '~/hooks-data/use-user-role'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useSelectedViewControls } from '../blocks/list/views/use-selected-view'
import { getViewDefaultTitle } from '@basehub/data/replicache/schema/block/list/utils'
import { editorCanEditBlocks } from '@basehub/data/replicache/schema/block/permissions'
import {
  useWorkingTreeStore,
  useWorkingTreeStoreRoot,
} from '~/hooks-data/blocks-tree/provider'
import { Replicache } from 'replicache'
import { RepoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import isEqual from 'lodash/isEqual'
import { blockIsFavorite } from './fav-utils'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'

type FavsTreeProps = {
  className?: string
  blockTreeManager: TreeManager<Block, NonNullable<unknown>>
  activeKey: { label: string; id: string }
  onActiveKeyChange: (
    key: string,
    opts: { autoFocus: boolean; partiallyOpen: boolean }
  ) => void
  selectedKeys: Set<{ label: string; id: string }>
  setSelectedKeys: React.Dispatch<
    React.SetStateAction<Set<{ label: string; id: string }>>
  >
  onUnselectDocsTree: () => void
}

export const FavsTree = ({
  className,
  blockTreeManager,
  activeKey,
  onActiveKeyChange,
  selectedKeys,
  setSelectedKeys,
  onUnselectDocsTree,
}: FavsTreeProps) => {
  const { data } = useRepoReplicache()
  const favoriteBlocksState = useUserPreferences((s) => s.favoriteBlocks)
  const userId = useUserPreferences((s) => s.id)

  const favoriteBlocks = useWorkingTreeStore((s) => {
    return (
      favoriteBlocksState?.map((bId) => s.tree?.getNode(bId)).filter(Boolean) ??
      []
    )
  }, isEqual)

  useCustomSyncerSub(
    !!favoriteBlocksState?.length && blockTreeManager.root.key
      ? data?.replicache
      : undefined,
    {
      syncer: 'loadBlocks',
      params: {
        rootBlockId: blockTreeManager.root.key,
        sourceBlockIds: favoriteBlocksState ?? [],
      },
    }
  )

  const { getControls: getListViewControls } = useSelectedViewControls()

  const activeBlockPath = useWorkingTreeStore((s) => {
    return (
      s.tree?.getPath(activeKey.id, {
        format: 'array',
      }) ?? []
    )
  }, isEqual)

  const renderControllerRef = React.useRef<Record<string, () => void>>({})
  // const groupsCache = React.useRef<Map<string, {}>>(new Set())
  const childrenToRenderCache = React.useRef<
    Map<string, { id: string; type: string }[] | null>
  >(new Map())
  const store = useWorkingTreeStoreRoot()

  React.useEffect(() => {
    const unsub = store.subscribe((s) => {
      const blockTreeManager = s.tree
      if (!blockTreeManager) return
      for (const [key, children] of childrenToRenderCache.current.entries()) {
        const debug = key === '7YTM2C7sKfR5hAT3MnaNK'
        try {
          const _children =
            // yes, this is weird but it's the only reliable way i found for the children keys change to trigger a re-render here
            // now that blockTreeManager doesn't auto-rerender the whole app...
            (blockTreeManager?.getChildrenKeys(key) ?? [])
              .map((ch) => {
                const node = blockTreeManager.getNode(ch)
                if (!node) return null
                return {
                  id: node.value.id,
                  type: node.value.type,
                }
              })
              .filter(Boolean)
          if (debug) {
            console.log({ _children, children })
          }

          if (isEqual(children, _children)) continue
          if (debug) {
            console.log(
              'about to call rerender...',
              key,
              renderControllerRef.current
            )
          }
          const renderFn = renderControllerRef.current?.[`r_${key}`]
          renderFn?.()
        } catch (err) {
          if (debug) {
            console.error(err)
          }
          childrenToRenderCache.current.delete(key)
          // eslint-disable-next-line react-compiler/react-compiler
          delete renderControllerRef.current?.[`r_${key}`]
        }
      }
    })

    return () => {
      unsub()
    }
  }, [store])

  if (!favoriteBlocks || favoriteBlocks.length === 0) return null
  return (
    <ChildContext.Provider value={{ replicache: data?.replicache }}>
      <TreePrimitive.Tree
        treeKey="favs-tree"
        treeManager={blockTreeManager as TreeManager<Block | BlockChange>}
        getKey={(node) => idToSourceBlockId(node.value as Block)}
        dragAndDrop="enabled-drop-after"
        onDragEnd={async ({ draggedItems }) => {
          invariant(userId, 'user preferences was not found')
          invariant(data?.replicache, 'replicache was not found')
          // make favorites
          await data.replicache.mutate.makeBlocksFavorite({
            blockIds: Array.from(draggedItems.keysDragged).map(({ id }) =>
              idToSourceBlockId(id, blockTreeManager)
            ),
            userId,
          })
        }}
        activeKey={activeKey}
        onActiveKeyChange={({ id }, { doubleClick }) => {
          onActiveKeyChange(id, {
            autoFocus: false,
            partiallyOpen: !doubleClick,
          })
        }}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        className={className}
      >
        <TreePrimitive.TreeTitle
          nestedItemsQuantity={favoriteBlocks.length}
          showActionsOnHover={false}
        >
          Favorites
        </TreePrimitive.TreeTitle>
        <TreePrimitive.TreeList
          onFocus={() => {
            // when the user focuses this tree, we want to unselect the other one
            onUnselectDocsTree()
          }}
        >
          <TreePrimitive.TreeListChild
            parent={blockTreeManager.root}
            emptyState={(level) => (
              <p className={clsx(level === 0 ? 'ml-1.5' : 'ml-2')}>No items</p>
            )}
            getChildrenToRender={(parent, _level, group) => {
              if (_level === 0) {
                const favoriteBlocks: TreeNode<Block>[] = []
                for (const child of blockTreeManager.itemsMap.values()) {
                  invariant(
                    favoriteBlocksState,
                    'user preferences was not found'
                  )
                  if (
                    blockIsFavorite({
                      block: child.value,
                      favoriteBlocks: favoriteBlocksState,
                    })
                  ) {
                    favoriteBlocks.push(child)
                  }
                }
                return favoriteBlocks
              }
              if (isBlockWithChildren(parent.value)) {
                const _children = (
                  blockTreeManager?.getChildrenKeys(parent.key) ?? []
                )
                  .map((ch) => {
                    const node = blockTreeManager.getNode(ch)
                    if (!node) return null
                    return {
                      id: node.value.id,
                      type: node.value.type,
                    }
                  })
                  .filter(Boolean)

                childrenToRenderCache.current.set(parent.key, _children)

                let children = _children.map((ch) => ch.id)

                if (isListBlock(parent.value)) {
                  if (group) {
                    const { selectedView } = getListViewControls(parent.value)
                    const isGroupActive =
                      selectedView === undefined ||
                      selectedView?.id === group.key

                    if (isGroupActive) {
                      const workingTemplate = blockTreeManager.getNode(
                        parent.value.typeOptions.targetBlockId,
                        { required: true }
                      )

                      // Store template IDs for easier reference
                      const templateIds = [
                        workingTemplate.value.id,
                        workingTemplate.value.sourceBlockId,
                      ].filter(Boolean)

                      // Find which block in the active path exists in children
                      const activeBlock = children.find((c) =>
                        activeBlockPath.includes(c)
                      )
                      if (!activeBlock) {
                        return [] // No active block found in children
                      }

                      // First sort to ensure template is at the top if present
                      const sortedChildren = [...children].sort((a, b) => {
                        // If a is a template ID, it should come first
                        if (templateIds.includes(a)) return -1
                        // If b is a template ID, it should come first
                        if (templateIds.includes(b)) return 1
                        return 0
                      })

                      // Find index of active block in sorted array
                      const activeBlockIndex = sortedChildren.findIndex(
                        (c) => c === activeBlock
                      )

                      // Get prev and next blocks
                      const prev =
                        activeBlockIndex > 0
                          ? sortedChildren[activeBlockIndex - 1]
                          : null
                      const next =
                        activeBlockIndex < sortedChildren.length - 1
                          ? sortedChildren[activeBlockIndex + 1]
                          : null

                      // Create final array with just prev, active, and next blocks
                      const visibleBlocks = [prev, activeBlock, next].filter(
                        Boolean
                      )

                      // Convert IDs to nodes
                      return visibleBlocks
                        .map((c) => {
                          const node = blockTreeManager.getNode(c, {
                            required: true,
                            fallbackKey: 'sourceBlockId',
                          })

                          if (!isBlockWithChildren(node.value)) {
                            return null
                          }

                          return node
                        })
                        .filter(Boolean)
                    } else {
                      children = [] // Not active group, return empty array
                    }
                  }
                }

                // Default behavior for non-list blocks or when not in a group
                return children
                  .map((c) => {
                    const node = blockTreeManager.getNode(c, {
                      required: true,
                      fallbackKey: 'sourceBlockId',
                    })

                    if (!isBlockWithChildren(node.value)) return null
                    return node
                  })
                  .filter(Boolean)
                  .sort((a) => {
                    // Template blocks are always pinned to the top
                    if (
                      isListBlock(parent.value) &&
                      (parent.value.typeOptions.targetBlockId === a.value.id ||
                        parent.value.typeOptions.targetBlockId ===
                          a.value.sourceBlockId) &&
                      a.value.hidden
                    ) {
                      return -1
                    }
                    return 0
                  })
              }
              return null
            }}
            getGroups={(_parent, _level, { isParentActive }) => {
              const parent = { ..._parent }
              if (!isListBlock(parent.value)) return null
              const grandparent = parent.parentKey
                ? blockTreeManager.getNode(parent.parentKey)
                : null

              const hasViews = parent.value.value.views?.length
              const views = hasViews
                ? (parent.value.value.views ?? [DEFAULT_LIST_VIEW])
                : [DEFAULT_LIST_VIEW]

              const { selectedView, setSelectedView } = getListViewControls(
                parent.value
              )

              return views.map((view) => ({
                key: view.id,
                title: view.title || <span>{getViewDefaultTitle(view)}</span>,
                dim:
                  parent.value.hidden || (grandparent?.value.hidden ?? false),
                isActive:
                  isParentActive &&
                  (hasViews ? selectedView?.id === view.id : true),
                onClick: (group, { doubleClick }) => {
                  onActiveKeyChange(idToSourceBlockId(parent.value), {
                    autoFocus: false,
                    partiallyOpen: !doubleClick,
                  })
                  setSelectedView(group.key)
                },
              }))
            }}
            level={0}
          >
            {Child}
          </TreePrimitive.TreeListChild>
        </TreePrimitive.TreeList>
      </TreePrimitive.Tree>
    </ChildContext.Provider>
  )
}

const ChildContext = React.createContext<{
  replicache: Replicache<RepoSpaceMutators> | null | undefined
}>({ replicache: null })

const Child: TreePrimitive.TreeListChildProps<Block>['children'] = ({
  child: _child,
  setIsRenaming,
  selectedKeys,
  isSelected,
}) => {
  const { replicache } = React.useContext(ChildContext)
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const node = useWorkingTreeStore((s) => s.tree?.getNode(_child.key))
  const path = useWorkingTreeStore((s) =>
    node
      ? s.tree?.getPath(node.key, { format: 'string', required: true })
      : null
  )
  const parentNode = useWorkingTreeStore((s) =>
    node?.parentKey ? s.tree?.getNode(node?.parentKey) : null
  )
  const isMemberOrOwner = useIsMemberOrHigher()
  const isEditorOrHigher = useIsEditorOrHigher()

  if (!treeManager || !node || !path) return null

  if (!isBlockWithChildren(node.value)) {
    return null
  }

  const { displayTitle } = getBlockDisplayData({
    block: node.value,
    parentBlock: parentNode?.value,
    ignoreDisplayTitle: true,
  })
  const highlight = isComponentBlock(node.value)
  const dim = node.value.hidden || parentNode?.value.hidden

  const canToggleHidden =
    (isMemberOrOwner ||
      (editorCanEditBlocks({
        blocks: [node.value],
        parentBlock: parentNode?.value,
      }) &&
        isEditorOrHigher)) &&
    parentNode &&
    !shouldInheritHiddenState(
      node.value,
      path,
      (id) =>
        treeManager.getNode(id, {
          fallbackKey: 'sourceBlockId',
          required: true,
        }).value
    )

  // we don't show rich text children in the sidebar
  if (parentNode?.value.type === 'rich-text') return null
  return (
    <TreePrimitive.TreeListItem
      contextMenuActions={
        <ContextMenuActions
          blockTreeManager={treeManager}
          child={node}
          setIsRenaming={setIsRenaming}
          selectedKeys={selectedKeys}
          isSelected={isSelected}
          canDuplicate={false}
        />
      }
    >
      <TreePrimitive.TreeListItemChevron
        saveExpandedStateInStorage={{
          type: 'sessionStorage',
          key: `fav-${idToSourceBlockId(node.value)}`,
        }}
        icon={
          isRichTextBlock(node.value)
            ? () => <span className="w-1 h-1 bg-[currentColor] rounded-full" />
            : undefined
        }
        className={
          isRichTextBlock(node.value) ? 'pointer-events-none' : undefined
        }
      />
      <TreePrimitive.TreeListItemIcon
        className={clsx(
          highlight && `text-dark-control-highlight`,
          dim && (highlight ? 'opacity-70' : 'opacity-50')
        )}
      >
        <BlockIcon block={node.value} />
      </TreePrimitive.TreeListItemIcon>
      <TreePrimitive.TreeListItemTitle
        className={clsx(
          highlight && `text-dark-control-highlight`,
          dim && (highlight ? 'opacity-70' : 'opacity-50')
        )}
      >
        {displayTitle}
      </TreePrimitive.TreeListItemTitle>

      <TreePrimitive.TreeListItemRenameInput
        defaultValue={node.value.title}
        onRename={async (value) => {
          await replicache?.mutate.updateBlock({
            id: node.value.id,
            instanceChildTarget: node.value.instanceChildTarget,
            data: {
              type: node.value.type,
              title: value,
            },
            strategy: 'merge',
            path,
            isoNow: new Date().toISOString(),
          })
        }}
      />

      <TreePrimitive.TreeListItemActions
        plusButtonOptions={{
          variant: 'disabled',
          dimmed: node.value.hidden,
        }}
      >
        {canToggleHidden && (
          <TreePrimitive.TreeListItemAction
            ariaLabel={node.value.hidden ? 'Show' : 'Hide'}
            icon={
              node.value.hidden ? (
                <EyeClosedIcon className="opacity-50" />
              ) : (
                <EyeOpenIcon />
              )
            }
            onClick={async () => {
              await replicache?.mutate.updateBlock({
                id: node.value.id,
                instanceChildTarget: node.value.instanceChildTarget,
                data: {
                  hidden: !node.value.hidden,
                },
                strategy: 'merge-deep',
                path: treeManager.getPath(node.key, {
                  format: 'string',
                  required: true,
                }),
                isoNow: new Date().toISOString(),
              })
            }}
            alwaysVisible={node.value.hidden}
          />
        )}
      </TreePrimitive.TreeListItemActions>
    </TreePrimitive.TreeListItem>
  )
}
